import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cur } from '@/lib/currency';

const fmt = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Card = ({ label, value, sub, tone = '', testid }) => (
  <div className="rounded-xl border border-border bg-card p-3 min-w-0" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{label}</div>
    <div className={`text-base font-bold tabular-nums ${tone}`}>{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
  </div>
);

// What this period really cost (profit) vs. money that only settles supplier balances (not profit).
export function ExpenseSummaryCards({ ct }) {
  const [open, setOpen] = useState(false);
  if (!ct) return null;
  const sc = ct.supplier_credit;
  return (
    <div className="space-y-2 mb-4" data-testid="expense-summary-cards">
      <div className="text-xs font-semibold text-muted-foreground">Affects profit (this period) · {ct.basis || 'excl. recoverable VAT'}</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Card label="Operating expenses" value={fmt(ct.operating_expenses)} sub={ct.operating_on_credit ? `${fmt(ct.operating_on_credit)} not yet paid` : 'salaries, rent, fees…'} testid="esc-operating" />
        <Card label="Purchases paid now" value={fmt(ct.purchases_paid_now)} sub="cash / bank at purchase" testid="esc-purchases-paid" />
        <Card label="Purchases on credit" value={fmt(ct.purchases_on_credit)} sub="added to supplier balance" testid="esc-purchases-credit" />
        <Card label="Total cost (profit basis)" value={fmt(ct.pnl_expense)} tone="text-error" sub="operating + purchases = P&L / Branch Summary" testid="esc-total-cost" />
        <Card label="Recoverable purchase VAT" value={fmt(ct.recoverable_vat)} sub="claimed in VAT return, not a cost" testid="esc-recoverable-vat" />
      </div>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-xs font-semibold text-primary" data-testid="esc-toggle-supplier">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Supplier repayments &amp; balances (do not reduce profit) · {fmt(ct.ap_settlement)} paid
      </button>
      {open && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="esc-supplier">
          <Card label="Paid against this period's credit" value={fmt(sc?.paid_current_credit)} testid="esc-paid-current" />
          <Card label="Paid against older credit" value={fmt(sc?.paid_previous_credit)} sub="settles opening balance" testid="esc-paid-old" />
          <Card label="Opening supplier balance" value={fmt(sc?.opening_outstanding)} testid="esc-opening" />
          <Card label="Closing supplier balance" value={fmt(sc?.closing_outstanding)} tone="text-rose-700" testid="esc-closing" />
          <Card label="Supplier payment rows (this list)" value={fmt(ct.ap_settlement)} sub={(sc?.advance_or_unmatched || 0) >= 0.01 ? `advances / no bill ${fmt(sc.advance_or_unmatched)}` : 'cash/bank movement only'} testid="esc-ap-rows" />
          {!sc && <div className="col-span-full text-[11px] text-muted-foreground">Pick a date range to see the split against current and older credit.</div>}
        </div>
      )}
    </div>
  );
}
